import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  Activity,
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  KeyRound,
  Pencil,
  Plus,
  Search,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  PERMISSIONS,
  createFaceScanKeystoreSchema,
  type CreateFaceScanKeystoreInput,
  type FaceScanKeystore,
  type FaceScanLookupResult,
} from '@pms/shared';
import { faceScanKeystoresService } from '@/services/faceScanKeystores.service';
import { usePermission } from '@/hooks/usePermission';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { env } from '@/config/env';
import { formatDateTimeIST } from '@/lib/dateFormat';
import { Notice } from '@/components/ui/notice';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { SearchInput } from '@/components/ui/search-input';
import { StatTile } from '@/components/ui/stat-tile';
import { DataTable, type Column } from '@/components/ui/data-table';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const QUERY_KEY = ['face-scan-keystores'] as const;
const PAGE_SIZE = 20;
/** Backend code for "the Rate API demanded a pass key" — PMS sends none, so retrying cannot help. */
const AUTH_REQUIRED_CODE = 'RATE_API_AUTH_REQUIRED';

type StatusFilter = 'all' | 'active' | 'inactive';

interface FormState {
  open: boolean;
  editing: FaceScanKeystore | null;
}

const EMPTY_FORM: CreateFaceScanKeystoreInput = { keystore_id: '', url: '', label: '', active: true };

type ApiErrorResponse = AxiosError<{ error?: { code?: string } }>['response'];

function errorResponse(e: unknown): ApiErrorResponse {
  // React Query reports "no error" as null — only an object can carry an Axios response.
  if (!e || typeof e !== 'object') return undefined;
  return (e as AxiosError<{ error?: { code?: string } }>).response;
}

function isAuthRequiredError(e: unknown): boolean {
  return errorResponse(e)?.data?.error?.code === AUTH_REQUIRED_CODE;
}

const retryUnlessAuthRequired = (count: number, error: unknown) => !isAuthRequiredError(error) && count < 2;

/** Rate API timestamps are SQLite strings in UTC without a zone marker ("2026-09-11 10:00:00"). */
function formatRateApiTime(value: string | null, empty = '—'): string {
  if (!value) return empty;
  const iso = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value.replace(' ', 'T')}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? value : formatDateTimeIST(date);
}

function lookupUrlFor(keystoreId: string): string {
  return `${env.RATE_API_URL.replace(/\/+$/, '')}/api/face-scan/url/${encodeURIComponent(keystoreId)}`;
}

async function copyText(text: string, what: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error('Could not copy to the clipboard');
  }
}

function showError(e: unknown) {
  toast.error(getErrorMessage(e));
}

/**
 * HRMS Config › Face Scan Keystores. The keystores live on the Rate API; the PMS backend
 * proxies every call without any pass key, and PMS permissions decide who may use this tab.
 */
export function FaceScanKeystoresSection() {
  const queryClient = useQueryClient();
  const { has } = usePermission();
  const canManage = has(PERMISSIONS.FACE_SCAN_KEYSTORES_MANAGE);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // Every list call re-reads the whole Rate API list, so wait for a pause in typing.
  const debouncedSearch = useDebouncedValue(search.trim());
  const [status, setStatus] = useState<StatusFilter>('all');
  const [form, setForm] = useState<FormState>({ open: false, editing: null });
  const [viewingId, setViewingId] = useState<string | null>(null);
  // Separate pending states: one confirm dialog must never mean two different actions.
  const [pendingOff, setPendingOff] = useState<FaceScanKeystore | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FaceScanKeystore | null>(null);
  const [pendingLookup, setPendingLookup] = useState<FaceScanKeystore | null>(null);
  const [lookupResult, setLookupResult] = useState<FaceScanLookupResult | null>(null);

  const listQuery = useQuery({
    queryKey: [...QUERY_KEY, 'list', { page, q: debouncedSearch, status }],
    queryFn: () =>
      faceScanKeystoresService.list({ page, limit: PAGE_SIZE, q: debouncedSearch || undefined, status }),
    retry: retryUnlessAuthRequired,
  });
  const summaryQuery = useQuery({
    queryKey: [...QUERY_KEY, 'summary'],
    queryFn: () => faceScanKeystoresService.summary(),
    retry: retryUnlessAuthRequired,
  });

  const loadError = listQuery.error ?? summaryQuery.error;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  const statusMutation = useMutation({
    mutationFn: ({ keystoreId, active }: { keystoreId: string; active: boolean }) =>
      faceScanKeystoresService.update(keystoreId, { active }),
    onSuccess: (row) => {
      toast.success(row.active ? `${row.keystore_id} switched on` : `${row.keystore_id} switched off`);
      setPendingOff(null);
      void invalidate();
    },
    onError: showError,
  });

  const deleteMutation = useMutation({
    mutationFn: (keystoreId: string) => faceScanKeystoresService.remove(keystoreId),
    onSuccess: (_r, keystoreId) => {
      toast.success(`${keystoreId} deleted`);
      setPendingDelete(null);
      void invalidate();
    },
    onError: showError,
  });

  const lookupMutation = useMutation({
    mutationFn: (keystoreId: string) => faceScanKeystoresService.lookup(keystoreId),
    onSuccess: (result) => {
      setLookupResult(result);
      void invalidate();
    },
    onError: showError,
  });

  const requestStatusChange = (keystore: FaceScanKeystore, next: boolean) => {
    if (next === keystore.active) return;
    if (next) {
      statusMutation.mutate({ keystoreId: keystore.keystore_id, active: true }); // no confirm needed
      return;
    }
    setPendingOff(keystore);
  };

  const summary = summaryQuery.data;
  const filtersActive = search.trim() !== '' || status !== 'all';

  const columns: Column<FaceScanKeystore>[] = [
    {
      key: 'label',
      header: 'Label',
      className: 'min-w-[9rem] max-w-[14rem]',
      cell: (k) =>
        k.label ? (
          <span className="block truncate font-medium" title={k.label}>{k.label}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'keystore_id',
      header: 'Keystore ID',
      className: 'whitespace-nowrap',
      cell: (k) => (
        <div className="flex items-center gap-1">
          <span className="font-mono text-xs">{k.keystore_id}</span>
          <Button
            size="icon"
            variant="ghost"
            aria-label={`Copy ${k.keystore_id}`}
            onClick={() => void copyText(k.keystore_id, 'Keystore ID')}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
    {
      key: 'url',
      header: 'URL',
      className: 'min-w-[12rem] max-w-[18rem]',
      cell: (k) => (
        <a
          href={k.url}
          target="_blank"
          rel="noreferrer"
          title={k.url}
          className="flex min-w-0 items-center gap-1 text-primary hover:underline"
        >
          <span className="truncate">{k.url}</span>
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      className: 'w-28 whitespace-nowrap',
      cell: (k) =>
        canManage ? (
          <div className="flex items-center gap-2">
            <Switch
              size="sm"
              checked={k.active}
              disabled={statusMutation.isPending}
              onChange={(next) => requestStatusChange(k, next)}
              aria-label={`${k.active ? 'Switch off' : 'Switch on'} ${k.keystore_id}`}
            />
            <span className="text-xs text-muted-foreground">{k.active ? 'On' : 'Off'}</span>
          </div>
        ) : (
          <StatusPill active={k.active} />
        ),
    },
    {
      key: 'hits',
      header: 'Hits',
      className: 'w-16 whitespace-nowrap text-right',
      cell: (k) => <span className="tabular-nums">{k.hits.toLocaleString('en-IN')}</span>,
    },
    {
      key: 'last_used_at',
      header: 'Last used',
      className: 'whitespace-nowrap',
      cell: (k) => <span className="text-xs">{formatRateApiTime(k.last_used_at, 'Never')}</span>,
    },
    {
      key: 'updated_at',
      header: 'Updated',
      className: 'whitespace-nowrap',
      cell: (k) => <span className="text-xs">{formatRateApiTime(k.updated_at)}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'w-44 whitespace-nowrap text-right',
      cell: (k) => (
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" aria-label="View details" onClick={() => setViewingId(k.keystore_id)}>
            <Eye className="h-4 w-4" />
          </Button>
          {canManage && (
            <>
              <Button
                size="icon"
                variant="ghost"
                aria-label="Test lookup"
                disabled={lookupMutation.isPending}
                onClick={() => setPendingLookup(k)}
              >
                <Search className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" aria-label="Edit" onClick={() => setForm({ open: true, editing: k })}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="ghost" aria-label="Delete" onClick={() => setPendingDelete(k)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border/60 bg-card p-4 text-sm">
        <p className="font-medium">
          Keystores are stored on the Rate API ({env.RATE_API_URL.replace(/^https?:\/\//, '')})
        </p>
        <p className="mt-1 text-muted-foreground">
          Each keystore maps an ID to a URL. Kiosks and apps look the URL up by ID, so a change
          here reaches them on their next lookup without rebuilding anything.
        </p>
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          The Rate API takes these calls without any key, so anyone who knows its address can read
          or change keystores directly. PMS permissions only protect this tab — use IDs that are
          hard to guess.
        </p>
      </div>

      {loadError != null && (
        isAuthRequiredError(loadError) ? (
          <Notice tone="warning" title="The Rate API refused the request">
            {getErrorMessage(loadError)}. PMS calls the keystore API without a pass key, so this
            tab works again as soon as the Rate API stops requiring one.
          </Notice>
        ) : (
          <Notice tone="danger" title="Could not load keystores">
            {getErrorMessage(loadError)}
          </Notice>
        )
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Total" value={summary?.total ?? '—'} icon={KeyRound} tone="teal" />
        <StatTile label="Active" value={summary?.active ?? '—'} icon={CheckCircle2} tone="emerald" />
        <StatTile label="Inactive" value={summary?.inactive ?? '—'} icon={XCircle} tone="rose" />
        <StatTile
          label="Total lookups"
          value={summary ? summary.total_hits.toLocaleString('en-IN') : '—'}
          icon={Activity}
          tone="violet"
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="Search ID, label or URL…"
          containerClassName="w-full sm:max-w-sm"
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value as StatusFilter);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-40" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {canManage && (
          <Button className="sm:ml-auto" onClick={() => setForm({ open: true, editing: null })}>
            <Plus className="h-4 w-4" /> Add keystore
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={listQuery.data?.data ?? []}
        rowKey={(k) => k.keystore_id}
        loading={listQuery.isLoading}
        error={listQuery.isError}
        onRetry={() => void listQuery.refetch()}
        empty={
          filtersActive ? (
            'No keystores match these filters.'
          ) : (
            <div className="flex flex-col items-center gap-2">
              <span>No keystores yet.</span>
              {canManage && (
                <Button size="sm" onClick={() => setForm({ open: true, editing: null })}>
                  <Plus className="h-4 w-4" /> Add the first keystore
                </Button>
              )}
            </div>
          )
        }
        pagination={{
          page,
          totalPages: listQuery.data?.pagination.totalPages ?? 1,
          total: listQuery.data?.pagination.total,
          onPageChange: setPage,
        }}
      />

      <KeystoreFormDialog
        state={form}
        onClose={() => setForm({ open: false, editing: null })}
        onSaved={() => {
          setForm({ open: false, editing: null });
          void invalidate();
        }}
      />

      <KeystoreDetailDialog keystoreId={viewingId} onClose={() => setViewingId(null)} />

      <Dialog open={!!lookupResult} onOpenChange={(open) => { if (!open) setLookupResult(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lookup works</DialogTitle>
            <DialogDescription>
              The Rate API answered for <span className="font-mono">{lookupResult?.keystore_id}</span>.
            </DialogDescription>
          </DialogHeader>
          {lookupResult && <CopyableValue value={lookupResult.url} what="URL" />}
          <DialogFooter>
            <Button onClick={() => setLookupResult(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingOff}
        onOpenChange={(open) => { if (!open) setPendingOff(null); }}
        title={`Switch off "${pendingOff?.keystore_id}"?`}
        description="Lookups for this ID will be refused until it is switched back on, so kiosks using it stop getting their URL."
        confirmLabel="Switch off"
        onConfirm={() => {
          if (pendingOff) statusMutation.mutate({ keystoreId: pendingOff.keystore_id, active: false });
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open) setPendingDelete(null); }}
        title={`Delete "${pendingDelete?.keystore_id}"?`}
        description="This removes the keystore from the Rate API and cannot be undone. Anything still looking up this ID will get “not found”."
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.keystore_id);
        }}
      />

      <ConfirmDialog
        open={!!pendingLookup}
        onOpenChange={(open) => { if (!open) setPendingLookup(null); }}
        title={`Test lookup for "${pendingLookup?.keystore_id}"?`}
        description="This is a real lookup: the keystore's hits go up by 1 and its last-used time is updated."
        confirmLabel="Run lookup"
        destructive={false}
        onConfirm={() => {
          if (pendingLookup) lookupMutation.mutate(pendingLookup.keystore_id);
          setPendingLookup(null);
        }}
      />
    </div>
  );
}

function StatusPill({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700'
          : 'inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground'
      }
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function CopyableValue({ value, what }: { value: string; what: string }) {
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-md bg-muted px-2 py-1.5 text-xs" title={value}>
        {value}
      </code>
      <Button size="icon" variant="outline" aria-label={`Copy ${what}`} onClick={() => void copyText(value, what)}>
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function KeystoreDetailDialog({ keystoreId, onClose }: { keystoreId: string | null; onClose: () => void }) {
  const query = useQuery({
    queryKey: [...QUERY_KEY, 'detail', keystoreId],
    queryFn: () => faceScanKeystoresService.get(keystoreId as string),
    enabled: !!keystoreId,
    // Hits and last-used move with every kiosk lookup — always read them fresh.
    retry: retryUnlessAuthRequired,
  });

  const keystore = query.data;

  return (
    <Dialog open={!!keystoreId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-mono">{keystoreId}</DialogTitle>
          <DialogDescription>Live details from the Rate API.</DialogDescription>
        </DialogHeader>

        {query.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-4 animate-pulse rounded bg-muted/60" />
            ))}
          </div>
        )}

        {query.isError && (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <p className="text-sm text-destructive">{getErrorMessage(query.error)}</p>
            <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </div>
        )}

        {keystore && keystoreId && (
          <div className="space-y-4">
            <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-sm">
              <DetailRow label="Label">{keystore.label ?? '—'}</DetailRow>
              <DetailRow label="URL">
                <a href={keystore.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  {keystore.url}
                </a>
              </DetailRow>
              <DetailRow label="Status"><StatusPill active={keystore.active} /></DetailRow>
              <DetailRow label="Hits">{keystore.hits.toLocaleString('en-IN')}</DetailRow>
              <DetailRow label="Last used">{formatRateApiTime(keystore.last_used_at, 'Never')}</DetailRow>
              <DetailRow label="Created by">{keystore.created_by ?? '—'}</DetailRow>
              <DetailRow label="Created">{formatRateApiTime(keystore.created_at)}</DetailRow>
              <DetailRow label="Updated">{formatRateApiTime(keystore.updated_at)}</DetailRow>
            </dl>

            <div className="space-y-1.5">
              <Label>Public lookup URL</Label>
              <CopyableValue value={lookupUrlFor(keystoreId)} what="Lookup URL" />
            </div>

            <div className="space-y-1.5">
              <Label>Example</Label>
              <pre className="overflow-x-auto rounded-md bg-muted p-2 text-xs">{`curl ${lookupUrlFor(keystoreId)}`}</pre>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KeystoreFormDialog({
  state,
  onClose,
  onSaved,
}: {
  state: FormState;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = state.editing;

  const { register, handleSubmit, reset, setValue, setError, control, formState } =
    useForm<CreateFaceScanKeystoreInput>({
      resolver: zodResolver(createFaceScanKeystoreSchema),
      defaultValues: EMPTY_FORM,
      values: editing
        ? {
            keystore_id: editing.keystore_id,
            url: editing.url,
            label: editing.label ?? '',
            active: editing.active,
          }
        : EMPTY_FORM,
    });
  const active = useWatch({ control, name: 'active' });

  const mutation = useMutation({
    mutationFn: (input: CreateFaceScanKeystoreInput) =>
      editing
        ? faceScanKeystoresService.update(editing.keystore_id, {
            url: input.url,
            label: input.label ? input.label : null,
            active: input.active,
          })
        : faceScanKeystoresService.create({ ...input, label: input.label || undefined }),
    onSuccess: (row) => {
      toast.success(editing ? `${row.keystore_id} updated` : `${row.keystore_id} created`);
      reset(EMPTY_FORM);
      onSaved();
    },
    onError: (e) => {
      if (errorResponse(e)?.status === 409) {
        setError('keystore_id', { message: 'Keystore ID already exists' });
        return;
      }
      showError(e);
    },
  });

  const close = () => {
    reset(EMPTY_FORM);
    onClose();
  };

  return (
    <Dialog open={state.open} onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.keystore_id}` : 'New keystore'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Change where this ID points. The ID itself cannot change.'
              : 'Choose an ID the kiosk will look up, and the URL it should get back.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((values) => mutation.mutate(values))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="keystore_id">Keystore ID</Label>
            <Input
              id="keystore_id"
              placeholder="e.g. gate_1"
              className="font-mono"
              readOnly={!!editing}
              autoComplete="off"
              {...register('keystore_id')}
            />
            {formState.errors.keystore_id && (
              <p className="text-xs text-destructive">{formState.errors.keystore_id.message}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="url">URL</Label>
            <Input id="url" placeholder="https://…" autoComplete="off" {...register('url')} />
            {formState.errors.url && <p className="text-xs text-destructive">{formState.errors.url.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="label">Label</Label>
            <Input id="label" placeholder="Optional, e.g. Office Gate 1" {...register('label')} />
            {formState.errors.label && <p className="text-xs text-destructive">{formState.errors.label.message}</p>}
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border border-border/60 p-3">
            <div>
              <p className="text-sm font-medium">Active</p>
              <p className="text-xs text-muted-foreground">When off, lookups for this ID are refused.</p>
            </div>
            <Switch
              checked={!!active}
              onChange={(next) => setValue('active', next, { shouldDirty: true })}
              aria-label="Active"
            />
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
